import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface RequestNameAndFile {
  key: string;
  file?: string;
  type:
    | 'ReadyForCallOff'
    | 'Calloff'
    | 'OrderHistory'
    | 'ActiveOrders'
    | 'Claims'
    | 'Invoices'
    | 'Customer'
    | 'Customers'
    | 'StockQuantities'
    | 'Order'
    | 'StockDetails'
    | 'RollMap'
    | 'QualityReport'
    | 'Inquiries'
    | 'Inquiry'
    | 'StockPackageDetails'
    | 'DefaultValues'
    | 'Fallback'
    | 'Deliveries'
    | 'CustomerAddresses'
    | 'CreateCalloff'
    | 'StockDetails'
    | 'StockPackageDetails'
    | 'SegmentRelationships'
    | 'AccountMembers'
    | 'Users'
    | 'OrdersWithReports';
  datesToModify?: string[];
}

export const requestNamesAndDemoFiles: RequestNameAndFile[] = [
  {
    key: 'packagedetails',
    file: 'stockpackagedetails.json',
    type: 'StockPackageDetails',
  },
  {
    key: 'sales/stock/v1/details',
    file: 'stockdetails.json',
    type: 'StockDetails',
  },
  {
    key: 'deliveries',
    file: 'deliveries.json',
    type: 'Deliveries',
    datesToModify: ['dispatchedDate'],
  },
  {
    key: 'customeraddresses',
    file: 'customeraddresses.json',
    type: 'CustomerAddresses',
  },
  {
    key: 'createcalloff',
    file: 'createcalloff.json',
    type: 'CreateCalloff',
  },
  {
    key: 'defaultvalues',
    file: 'defaultvalues.json',
    type: 'DefaultValues',
    datesToModify: [
      'inquiryDate',
      'modifiedDate',
      'reservationDate',
      'preferredDeliveryDate',
      'deliveryDate',
      'originalDeliveryDate',
      'readyDate',
    ],
  },
  {
    key: 'sales/stock/v1/warehouse',
    file: 'inwarehouseorderrowstockquantities.json',
    type: 'ReadyForCallOff',
    datesToModify: [
      'orderHeaderRow.orderDate',
      'orderHeaderRow.createdDate',
      'orderHeaderRow.modifiedDate',
      'orderHeaderRow.preferredDeliveryDate',
      'orderHeaderRow.deliveryDate',
      'rowModifiedDate',
    ],
  },
  {
    key: 'sales/calloff',
    file: 'calloffs.json',
    type: 'Calloff',
    datesToModify: ['createdDate', 'preferredDeliveryDate', 'estimatedDeliveryDate'],
  },
  {
    key: 'orderstatuses=Delivered',
    file: 'orderhistory.json',
    type: 'OrderHistory',
    datesToModify: [
      'deliveryDate',
      'createdDate',
      'orderDate',
      'modifiedDate',
      'preferredDeliveryDate',
      'rowModifiedDate',
    ],
  },
  {
    key: 'orderstatuses=New',
    file: 'orderrows.json',
    type: 'ActiveOrders',
    datesToModify: [
      'deliveryDate',
      'createdDate',
      'orderDate',
      'modifiedDate',
      'preferredDeliveryDate',
      'rowModifiedDate',
    ],
  },
  {
    key: 'api/claims',
    file: 'claims.json',
    type: 'Claims',
    datesToModify: ['createdDate'],
  },
  {
    key: 'api/invoices',
    file: 'invoices.json',
    type: 'Invoices',
    datesToModify: ['invoiceDate', 'dueDate', 'paymentDate'],
  },
  { key: 'api/customers/', file: 'customer.json', type: 'Customer' },
  { key: 'api/customers?', file: 'customer.json', type: 'Customers' },
  {
    key: 'sales/stock/v1/customers',
    file: 'stockquantities.json',
    type: 'StockQuantities',
  },
  {
    key: 'orderswithreports',
    file: 'orderswithreports.json',
    type: 'OrdersWithReports',
  },
  {
    key: 'orders',
    file: 'order.json',
    type: 'Order',
    datesToModify: [
      'orderDate',
      'modifiedDate',
      'reservationDate',
      'preferredDeliveryDate',
      'deliveryDate',
      'originalDeliveryDate',
      'readyDate',
    ],
  },
  { key: 'stockdetails', file: 'stockdetails.json', type: 'StockDetails' },
  { key: 'api/reports/rollmap', file: 'rollmap.json', type: 'RollMap' },
  {
    key: 'api/reports/quality',
    file: 'quality.json',
    type: 'QualityReport',
    datesToModify: ['manufacturingDates', 'reportDate'],
  },
  {
    key: 'inquiryheaderrows',
    file: 'inquiries.json',
    type: 'Inquiries',
    datesToModify: [
      'inquiryDate',
      'reservationDate',
      'modifiedDate',
      'preferredDeliveryDate',
      'deliveryDate',
      'originalDeliveryDate',
      'readyDate',
    ],
  },
  {
    key: 'api/inquiries/inquiry',
    file: 'inquiry.json',
    type: 'Inquiry',
    datesToModify: [
      'inquiryDate',
      'createdDate',
      'modifiedDate',
      'preferredDeliveryDate',
      'deliveryDate',
      'rowModifiedDate',
    ],
  },
  {
    key: 'stockpackagedetails',
    file: 'stockpackagedetails.json',
    type: 'StockPackageDetails',
  },
  {
    key: 'users',
    type: 'Users',
  },
  {
    key: 'segmentrelationships',
    type: 'SegmentRelationships',
  },
  {
    key: 'accountmembers',
    type: 'AccountMembers',
  },
];

@Injectable({ providedIn: 'root' })
export class DebugInterceptor implements HttpInterceptor {
  constructor(private router: Router) {}
  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      tap((event: any) => {
        if (!(this.router.url.indexOf('debug') > 0) && !environment.debug) return;
        if (!event.ok) return;

        const reqType = requestNamesAndDemoFiles.find(
          (name: RequestNameAndFile) =>
            req.urlWithParams.includes(name.key) ||
            (name.file && req.urlWithParams.includes(name.file)),
        ) || { key: 'req', type: 'Request', file: '' };

        const sourceSystem =
          req.urlWithParams.includes('sourcesystemcode=1') ||
          req.urlWithParams.includes('sourcesystemid=1')
            ? 'Bond'
            : req.urlWithParams.includes('sourcesystemcode=2') ||
                req.urlWithParams.includes('sourcesystemid=2')
              ? 'Tips'
              : req.urlWithParams.includes('sourcesystemcode=3') ||
                  req.urlWithParams.includes('sourcesystemid=3')
                ? 'Dynamics CRM'
                : '';
        const customerId = req.urlWithParams.slice(
          req.urlWithParams.indexOf('ACC'),
          req.urlWithParams.indexOf('ACC') + 16,
        );

        const apiVersion = req.urlWithParams.includes('litium') ? 'Litium' : 'Sales';

        const params = req.params.toString().split('&');

        const empty = event?.body?.length === 0 ? '(empty)' : '';

        const group = `${reqType.type} ${sourceSystem} ${apiVersion} ${empty} `;

        // Group response in console
        console.groupCollapsed(group);
        const log = {
          customerId,
          params,
          url: req.urlWithParams,
          subscriptionKey: req.headers.get('ocp-apim-subscription-key'),
          token: req.headers.get('authorization'),
        };
        console.log(log);
        console.log(event.body);
        console.groupEnd();
      }),
    );
  }
}
